import { extractApiErrorMessage } from '../utils/apiErrors'
import type { ApiErrorPayload } from '../types/apiError'

const CSRF_HEADER_NAME = 'X-CSRF-Token'
const CSRF_TOKEN_ENDPOINT_PATH = '/api/auth/csrf-token'
const CSRF_TOKEN_ERROR_MESSAGE = 'Не удалось получить CSRF token.'
const CSRF_TOKEN_INVALID_CODE = 'csrf_token_invalid'
const UNSAFE_HTTP_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const DEFAULT_STATUS_ERROR_MESSAGES: Partial<Record<number, string>> = {
  401: 'Сессия больше не действительна. Выполните вход снова.',
  403: 'Недостаточно прав для выполнения операции.',
  409: 'Конфликт данных. Обновите страницу и попробуйте снова.'
}

let csrfTokenCache: string | null = null
let csrfTokenRequest: Promise<string> | null = null
const apiErrorHandlers = new Set<ApiErrorHandler>()

export class ApiHttpError extends Error {
  status: number
  payload: unknown
  handled: boolean

  constructor(status: number, payload: unknown, fallbackMessage: string = 'Ошибка API.') {
    const normalizedPayload = payload && typeof payload === 'object' ? (payload as ApiErrorPayload) : null
    super(extractApiErrorMessage(normalizedPayload, status, fallbackMessage))
    this.name = 'ApiHttpError'
    this.status = status
    this.payload = payload
    this.handled = false
  }

  markHandled(): void {
    this.handled = true
  }
}

export function isApiHttpError(error: unknown): error is ApiHttpError {
  return error instanceof ApiHttpError
}

export function isHandledApiError(error: unknown): error is ApiHttpError {
  return isApiHttpError(error) && error.handled
}

export interface ApiErrorRequestContext {
  method: string
  url: string
}

export type ApiErrorHandler = (error: ApiHttpError, context: ApiErrorRequestContext) => boolean | void

export function registerApiErrorHandler(handler: ApiErrorHandler): () => void {
  apiErrorHandlers.add(handler)

  return () => {
    apiErrorHandlers.delete(handler)
  }
}

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  headers?: Record<string, string>
  body?: unknown
  signal?: AbortSignal
  fallbackErrorMessage?: string
  statusErrorMessages?: Partial<Record<number, string>>
  skipGlobalErrorHandling?: boolean
  skipHandledStatuses?: number[]
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null

  const contentType = response.headers.get('content-type')?.toLowerCase() || ''
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null)
  }

  const text = await response.text().catch(() => '')
  return text || null
}

function isUnsafeMethod(method: string): boolean {
  return UNSAFE_HTTP_METHODS.has(method.toUpperCase())
}

function resolveRequestUrl(requestUrl: string): URL {
  try {
    return new URL(requestUrl)
  } catch {
    const baseOrigin = globalThis.location?.origin
    if (!baseOrigin) {
      throw new TypeError(`Invalid request URL: ${requestUrl}`)
    }

    return new URL(requestUrl, baseOrigin)
  }
}

function getCsrfTokenUrl(requestUrl: string): string {
  return new URL(CSRF_TOKEN_ENDPOINT_PATH, resolveRequestUrl(requestUrl)).toString()
}

function resolveFallbackErrorMessage(status: number, options: ApiRequestOptions): string {
  return options.statusErrorMessages?.[status]
    ?? options.fallbackErrorMessage
    ?? DEFAULT_STATUS_ERROR_MESSAGES[status]
    ?? 'Ошибка API.'
}

function handleApiError(error: ApiHttpError, options: ApiRequestOptions, context: ApiErrorRequestContext): ApiHttpError {
  if (options.skipGlobalErrorHandling) {
    return error
  }

  const skippedStatuses = new Set(options.skipHandledStatuses ?? [])
  if (skippedStatuses.has(error.status)) {
    return error
  }

  for (const handler of apiErrorHandlers) {
    const handled = handler(error, context)
    if (handled) {
      error.markHandled()
    }
  }

  return error
}

async function fetchCsrfToken(requestUrl: string, options: ApiRequestOptions): Promise<string> {
  const response = await fetch(getCsrfTokenUrl(requestUrl), {
    method: 'GET',
    credentials: 'include'
  })

  const payload = await parseBody(response)
  if (!response.ok) {
    throw handleApiError(
      new ApiHttpError(response.status, payload, CSRF_TOKEN_ERROR_MESSAGE),
      options,
      { method: 'GET', url: getCsrfTokenUrl(requestUrl) }
    )
  }

  if (!payload || typeof payload !== 'object' || typeof (payload as { csrfToken?: unknown }).csrfToken !== 'string') {
    throw new Error('Некорректный формат ответа API: отсутствует csrfToken.')
  }

  return (payload as { csrfToken: string }).csrfToken
}

async function getCsrfToken(requestUrl: string, options: ApiRequestOptions): Promise<string> {
  if (csrfTokenCache) {
    return csrfTokenCache
  }

  if (!csrfTokenRequest) {
    csrfTokenRequest = fetchCsrfToken(requestUrl, options)
      .then((token) => {
        csrfTokenCache = token
        return token
      })
      .finally(() => {
        csrfTokenRequest = null
      })
  }

  return csrfTokenRequest
}

function invalidateCsrfToken(rejectedToken: string): void {
  if (csrfTokenCache === rejectedToken) {
    csrfTokenCache = null
  }
}

async function isCsrfTokenRejection(response: Response): Promise<boolean> {
  if (response.status !== 400) {
    return false
  }

  const contentType = response.headers.get('content-type')?.toLowerCase() || ''
  if (!contentType.includes('application/json')) {
    return false
  }

  const payload: unknown = await response.clone().json().catch(() => null)
  return Boolean(payload)
    && typeof payload === 'object'
    && (payload as { code?: unknown }).code === CSRF_TOKEN_INVALID_CODE
}

async function applySecurityHeaders(
  url: string,
  method: string,
  headers: Record<string, string>,
  options: ApiRequestOptions
): Promise<string | null> {
  if (!isUnsafeMethod(method)) {
    return null
  }

  const csrfToken = await getCsrfToken(url, options)
  headers[CSRF_HEADER_NAME] = csrfToken
  return csrfToken
}

function normalizeRequestBody(body: unknown, headers: Record<string, string>): BodyInit | undefined {
  if (body === undefined || body === null) return undefined
  if (typeof body === 'string' || body instanceof FormData || body instanceof Blob || body instanceof URLSearchParams) {
    return body
  }

  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json'
  }

  return JSON.stringify(body)
}

export function clearCsrfTokenCache(): void {
  csrfTokenCache = null
  csrfTokenRequest = null
}

async function sendRequest(
  url: string,
  method: string,
  options: ApiRequestOptions
): Promise<{ response: Response; csrfToken: string | null }> {
  const headers = { ...(options.headers || {}) }
  const csrfToken = await applySecurityHeaders(url, method, headers, options)
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: normalizeRequestBody(options.body, headers),
    signal: options.signal
  })

  return { response, csrfToken }
}

async function performRequest(
  url: string,
  options: ApiRequestOptions = {}
): Promise<{ response: Response; method: string }> {
  const method = options.method || 'GET'
  const { response, csrfToken } = await sendRequest(url, method, options)

  if (csrfToken === null || options.signal?.aborted || !(await isCsrfTokenRejection(response))) {
    return { response, method }
  }

  invalidateCsrfToken(csrfToken)
  const retry = await sendRequest(url, method, options)
  return { response: retry.response, method }
}

export async function requestJson<TResponse>(url: string, options: ApiRequestOptions = {}): Promise<TResponse> {
  const { response, method } = await performRequest(url, options)

  const payload = await parseBody(response)
  if (!response.ok) {
    throw handleApiError(
      new ApiHttpError(response.status, payload, resolveFallbackErrorMessage(response.status, options)),
      options,
      { method, url }
    )
  }

  return payload as TResponse
}

export async function requestEventStream(url: string, options: ApiRequestOptions = {}): Promise<Response> {
  const headers = {
    Accept: 'text/event-stream',
    ...(options.headers || {})
  }
  const { response, method } = await performRequest(url, {
    ...options,
    method: 'GET',
    headers
  })

  if (!response.ok) {
    const payload = await parseBody(response)
    throw handleApiError(
      new ApiHttpError(response.status, payload, resolveFallbackErrorMessage(response.status, options)),
      options,
      { method, url }
    )
  }

  return response
}

export async function requestBlob(url: string, options: ApiRequestOptions = {}): Promise<{ blob: Blob; headers: Headers }> {
  const { response, method } = await performRequest(url, options)

  if (!response.ok) {
    const payload = await parseBody(response)
    throw handleApiError(
      new ApiHttpError(response.status, payload, resolveFallbackErrorMessage(response.status, options)),
      options,
      { method, url }
    )
  }

  return { blob: await response.blob(), headers: response.headers }
}

export async function requestVoid(url: string, options: ApiRequestOptions = {}): Promise<void> {
  const { response, method } = await performRequest(url, options)

  if (!response.ok) {
    const payload = await parseBody(response)
    throw handleApiError(
      new ApiHttpError(response.status, payload, resolveFallbackErrorMessage(response.status, options)),
      options,
      { method, url }
    )
  }
}
